import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import '../../assets/css/agent-custom.css';
import loginBg from '../../assets/img/login-bg.png';

export default function CreateAccount() {
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'buyer' });
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otp, setOtp] = useState('');

    const handleSendOTP = async () => {
        if (!form.email) {
            setMsg({ type: 'error', text: 'Please enter your email address first.' });
            return;
        }
        setOtpLoading(true);
        setMsg(null);
        try {
            const data = await api.sendSignupOTP(form.email);
            if (data.success) {
                setOtpSent(true);
                setMsg({ type: 'success', text: 'OTP has been sent to your email! Please check your inbox.' });
            } else {
                setMsg({ type: 'error', text: data.message || 'Failed to send OTP.' });
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Could not send OTP.' });
        }
        setOtpLoading(false);
    };

    const roles = [
        { id: 'buyer', label: 'Buyer', icon: 'fa-house-user' },
        { id: 'tenant', label: 'Tenant', icon: 'fa-key' },
        { id: 'broker', label: 'Broker', icon: 'fa-handshake' },
        { id: 'owner', label: 'Owner', icon: 'fa-building-user' },
        { id: 'developer', label: 'Developer', icon: 'fa-city' }
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleRoleSelect = (roleId: string) => {
        setForm(prev => ({ ...prev, role: roleId }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMsg(null);
        
        // Step 1: Send OTP if not sent yet
        if (!otpSent) {
            setLoading(true);
            try {
                const data = await api.sendSignupOTP(form.email);
                if (data.success) {
                    setOtpSent(true);
                    setMsg({ type: 'success', text: 'Verification code sent to your email! Please enter it below to complete registration.' });
                } else {
                    setMsg({ type: 'error', text: data.message || 'Failed to send OTP.' });
                }
            } catch (err) {
                setMsg({ type: 'error', text: 'Server error. Could not send OTP.' });
            }
            setLoading(false);
            return;
        }

        // Step 2: Verify OTP and Register
        if (!otp) {
            setMsg({ type: 'error', text: 'Please enter the 6-digit verification code.' });
            return;
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(form.password)) {
            setMsg({ 
                type: 'error', 
                text: 'Password must be at least 8+ characters, including 1 uppercase, 1 lowercase, 1 number, and 1 special character.' 
            });
            return;
        }
        
        setLoading(true);
        try {
            const data = await api.register({ ...form, otp });
            if (data.token) {
                setMsg({ type: 'success', text: 'Account verified and created successfully! Redirecting...' });
                setTimeout(() => navigate('/login'), 1500);
            } else {
                setMsg({ type: 'error', text: data.message || 'Registration failed.' });
            }
        } catch {
            setMsg({ type: 'error', text: 'Server error. Please try again.' });
        }
        setLoading(false);
    };

    return (
        <div className="premium-auth-wrapper">
            {/* Left Side: Branding & Image */}
            <div className="auth-image-side">
                <img src={loginBg} alt="Luxury Real Estate" />
                <div className="auth-image-overlay">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <h1>Your Property <br />Journey Starts Here</h1>
                        <p>Register now to unlock the best deals, verified listings, and expert guidance.</p>
                    </motion.div>
                </div>
            </div>

            {/* Right Side: Registration Form */}
            <div className="auth-form-side !flex-[1.1]">
                <motion.div 
                    className="glass-auth-card !max-w-[550px]"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="auth-logo mb-6 flex justify-center">
                        <Link to="/">
                            <img src="/white-logo.png" className="img-fluid" width="160" alt="Property Guru" />
                        </Link>
                    </div>

                    <div className="premium-auth-header text-center !mb-6">
                        <motion.h2
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            Create Account
                        </motion.h2>
                        <motion.p
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            className="text-white/60 text-xs"
                        >
                            Select your role and enter your details to get started
                        </motion.p>
                    </div>

                    {msg && (
                        <motion.div 
                            className={`alert ${msg.type === 'success' ? 'alert-success bg-emerald-500/10 text-emerald-400' : 'alert-danger bg-danger/10 text-danger'} border-0 rounded-4 mb-4 small fw-bold text-center py-3`} 
                            role="alert"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <i className={`fa-solid ${msg.type === 'success' ? 'fa-check' : 'fa-circle-exclamation'} me-2`}></i>
                            {msg.text}
                        </motion.div>
                    )}

                    <form onSubmit={handleSubmit} className="premium-auth-form">
                        
                        {/* Role Selection Container */}
                        <div className="auth-form-group mb-4">
                            <label className="text-center w-100 !text-[10px] opacity-70 mb-3">I am joining as a</label>
                            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                                {roles.map(role => (
                                    <div 
                                        key={role.id}
                                        onClick={() => handleRoleSelect(role.id)}
                                        className={`flex flex-col items-center justify-center p-2 rounded-xl border transition-all cursor-pointer ${
                                            form.role === role.id 
                                            ? 'border-blue-500 bg-blue-500/20 text-white' 
                                            : 'border-white/10 bg-white/5 text-white/40 hover:bg-white/10'
                                        }`}
                                    >
                                        <i className={`fa-solid ${role.icon} ${form.role === role.id ? 'text-base md:text-xl' : 'text-sm md:text-lg'} mb-1`}></i>
                                        <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-tighter text-center leading-tight">{role.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <div className="auth-form-group mb-0">
                                    <label>{form.role === 'developer' ? 'Company' : 'Full Name'}</label>
                                    <div className="input-with-icon">
                                        <i className="fa-regular fa-user input-icon"></i>
                                        <input
                                            type="text"
                                            name="name"
                                            className="premium-auth-input !py-3 !text-sm !text-white"
                                            placeholder="Enter your name"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="auth-form-group mb-0">
                                    <label>Email Address</label>
                                    <div className="input-with-icon">
                                        <i className="fa-regular fa-envelope input-icon"></i>
                                        <input
                                            type="email"
                                            name="email"
                                            className="premium-auth-input !py-3 !text-sm w-full !text-white"
                                            placeholder="example@example.com"
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                            disabled={otpSent}
                                        />
                                    </div>
                                </div>
                            </div>
                            {otpSent && (
                                <div className="col-md-12">
                                    <motion.div 
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        className="auth-form-group mb-0 mt-2"
                                    >
                                        <label className="text-blue-400">Enter Email Verification Code (OTP)</label>
                                        <div className="input-with-icon">
                                            <i className="fa-solid fa-key input-icon text-blue-400"></i>
                                            <input
                                                type="text"
                                                className="premium-auth-input !py-3 !text-sm border-blue-500/30 bg-blue-500/5 focus:border-blue-500 !text-white"
                                                placeholder="Enter 6-digit code"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value)}
                                                maxLength={6}
                                                required
                                            />
                                            <button 
                                                type="button" 
                                                onClick={() => { setOtpSent(false); setOtp(''); }}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-bold text-white/40 uppercase tracking-widest hover:text-white"
                                            >
                                                Change Email
                                            </button>
                                        </div>
                                    </motion.div>
                                </div>
                            )}
                            <div className="col-md-6">
                                <div className="auth-form-group mb-0">
                                    <label>Phone Number</label>
                                    <div className="input-with-icon">
                                        <i className="fa-solid fa-phone input-icon"></i>
                                        <input
                                            type="tel"
                                            name="phone"
                                            className="premium-auth-input !py-3 !text-sm !text-white"
                                            placeholder="+91 98765..."
                                            value={form.phone}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="auth-form-group mb-0">
                                    <label>Security Key</label>
                                    <div className="input-with-icon">
                                        <i className="fa-solid fa-lock input-icon"></i>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            className="premium-auth-input !py-3 !text-sm !text-white"
                                            placeholder="••••••••"
                                            value={form.password}
                                            onChange={handleChange}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle !right-4"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-[10px]`}></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-check mb-4">
                            <input className="form-check-input bg-transparent border-white/20" type="checkbox" id="terms" required />
                            <label className="form-check-label small text-white/70 cursor-pointer" htmlFor="terms">
                                I agree to the <Link to="/terms" className="text-blue-400 fw-bold">Terms</Link> & <Link to="/privacy-policy" className="text-blue-400 fw-bold">Privacy</Link>
                            </label>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            className="premium-btn-auth !py-3 !text-sm"
                            disabled={loading}
                        >
                            {loading ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span>{otpSent ? 'Verifying...' : 'Sending Code...'}</>
                            ) : (
                                <>{otpSent ? 'Verify & Register' : 'Register Now'} <i className="fa-solid fa-arrow-right-long ms-2"></i></>
                            )}
                        </motion.button>

                        <div className="text-center mt-4 pt-2 border-t border-white/10">
                            <p className="mb-0 small text-white/60">
                                Already registered? <Link to="/login" className="text-white fw-black text-decoration-none hover-underline ms-1 uppercase tracking-wider">Sign in here</Link>
                            </p>
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
}
